/**
 * The built-in base-cabinet run (defs/baseCabinet.ts) as a data spec.
 *
 * The user draws a run along a wall (LINE anchor + WALL constraint); the run
 * is skinned into as many modules of about `moduleWidth` as fit (at least
 * one), stretched evenly so they fill the drawn length exactly:
 *  - `modules` = max(1, round(width / moduleWidth)), `moduleSize` = width / modules;
 *  - the carcass outline follows the footprint (a stretch group);
 *  - the worktop overhangs the carcass by a FIXED 2 cm at the front and both
 *    ends — a formula-driven `fixed` group, shown only with `worktop` on;
 *  - dividers sit BETWEEN modules — an `index` repeat drawing `modules - 1`
 *    lines at `(i + 1) · moduleSize`;
 *  - door swings (`doorsOpen`) — an `index` repeat with one open leaf and one
 *    quarter arc per module. The leaf length and the arc radius both equal
 *    the ACTUAL module width (so the swing stays round however the run is
 *    stretched), and hinges alternate ends (`mod(i, 2)`) so neighbouring
 *    doors swing toward each other.
 *
 * `moduleWidth` ↔ `moduleCount` are kept in line with the run length by a
 * step sync, like the code version: editing the count re-evens the module
 * width to width / count; anything else sets count := round(width / moduleWidth)
 * (and that count write re-evens the width in turn — no `exactStep`).
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from '../spec';

const W = 1.2;
const D = 0.6;
const HW = W / 2;
const HD = D / 2;
/** Worktop overhang past the carcass on the front and both ends (m). */
const OVERHANG = 0.02;
/** Module width at the default size and params (for the authored drawing). */
const MW = 0.6;

export const baseCabinetSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'LINE',
  constraint: { kind: 'WALL', defaultInset: 0 },
  defaultSize: { width: W, depth: D, height: 0.9 },
  params: [
    { key: 'moduleWidth', kind: 'float', label: 'module width', default: 0.6, min: 0.2, max: 1.2, decimals: 2, distance: true },
    { key: 'moduleCount', kind: 'int', label: 'number of cabinets', default: 2, min: 1 },
    { key: 'doorsOpen', kind: 'boolean', label: 'show door swings', default: false },
    { key: 'worktop', kind: 'boolean', label: 'worktop', default: true },
  ],
  formulas: [
    { key: 'overhang', label: 'Worktop overhang', expr: `${OVERHANG}`, note: 'Worktop overhang past the carcass at the front and both ends (m)' },
    { key: 'modules', label: 'Number of cabinets', expr: 'max(1, round(width / max(0.05, moduleWidth)))', note: 'How many modules of about the module width fit the run (at least one)' },
    { key: 'moduleSize', label: 'Cabinet width', expr: 'width / modules', note: 'Actual module width: the modules stretch evenly to fill the run' },
  ],
  shapes: [
    { id: 'carcass', name: 'Cabinet body', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    {
      id: 'worktop',
      name: 'Worktop',
      kind: 'rect',
      min: [-HW - OVERHANG, -HD],
      max: [HW + OVERHANG, HD + OVERHANG],
      expr: { 'min.u': '-width / 2 - overhang', 'min.n': '-depth / 2', 'max.u': 'width / 2 + overhang', 'max.n': 'depth / 2 + overhang' },
    },
    {
      id: 'divider',
      name: 'Divider',
      kind: 'polyline',
      points: [
        [-HW + MW, -HD],
        [-HW + MW, HD],
      ],
      closed: false,
      expr: {
        'p0.u': '-width / 2 + (i + 1) * moduleSize',
        'p0.n': '-depth / 2',
        'p1.u': '-width / 2 + (i + 1) * moduleSize',
        'p1.n': 'depth / 2',
      },
    },
    {
      id: 'doorLeaf',
      name: 'Door',
      kind: 'polyline',
      points: [
        [-HW, HD],
        [-HW, HD + MW],
      ],
      closed: false,
      // Hinge at the module's start for even modules, at its end for odd ones.
      expr: {
        'p0.u': '-width / 2 + (i + mod(i, 2)) * moduleSize',
        'p0.n': 'depth / 2',
        'p1.u': '-width / 2 + (i + mod(i, 2)) * moduleSize',
        'p1.n': 'depth / 2 + moduleSize',
      },
    },
    {
      id: 'doorSwing',
      name: 'Door swing',
      kind: 'arc',
      center: [-HW, HD],
      radius: MW,
      startAngle: Math.PI / 2,
      endAngle: 0,
      // From the open leaf tip (90°) back to the latch on the front face: 0° for a start hinge, 180° for an end hinge.
      expr: {
        'center.u': '-width / 2 + (i + mod(i, 2)) * moduleSize',
        'center.n': 'depth / 2',
        radius: 'moduleSize',
        startAngle: 'pi / 2',
        endAngle: 'mod(i, 2) * pi',
      },
    },
  ],
  groups: [
    { id: 'carcass', name: 'Cabinet body', shapeIds: ['carcass'], resize: { u: 'stretch', n: 'stretch' } },
    { id: 'worktop', name: 'Worktop', shapeIds: ['worktop'], resize: { u: 'fixed', n: 'fixed' }, visibleWhen: { param: 'worktop', equals: true } },
    { id: 'dividers', name: 'Dividers', shapeIds: ['divider'], resize: { u: 'fixed', n: 'fixed' }, repeat: { kind: 'index', count: { expr: 'modules - 1' } } },
    {
      id: 'doors',
      name: 'Door swings',
      shapeIds: ['doorLeaf', 'doorSwing'],
      resize: { u: 'fixed', n: 'fixed' },
      repeat: { kind: 'index', count: { expr: 'modules' } },
      visibleWhen: { param: 'doorsOpen', equals: true },
    },
  ],
  stepSyncs: [{ label: 'Cabinet width and count', step: 'moduleWidth', count: 'moduleCount', run: 'width', minCount: 1 }],
  model3d: null,
};
